import { ChevronLeftIcon } from '@heroicons/react/16/solid'
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '../catalyst/button'
import { Checkbox } from '../catalyst/checkbox'
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../catalyst/dialog'
import { Field as FormField, FieldGroup, Label } from '../catalyst/fieldset'
import { Heading } from '../catalyst/heading'
import { Input } from '../catalyst/input'
import { Link } from '../catalyst/link'
import { Select } from '../catalyst/select'
import { Text } from '../catalyst/text'
import { useAuth } from '../lib/auth'
import { useFarms } from '../lib/farms'
import { useField } from '../lib/field'
import { useHarvestLogs, type CreateHarvestLogInput } from '../lib/harvest-logs'
import { ComplianceBadge } from '../lib/compliance-badge'
import { Shell } from '../pages/shell'
import type { CanadaGapBlockingApplication, HarvestLog } from '../lib/types'
import { canadaGapBadgeProps, checkHarvest, draftNumber, exportP2, issueFor, useCanadaGapStatus, useEntryCheck } from '../lib/canadagap'
import { checkBadgeProps, EntryCheckPanel, InputWarning, SavedEntryNotice } from './entry-check'
import { EntryIssues } from './entry-issues'

const formatDate = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })

function HarvestLogDialog({
  open,
  onClose,
  initial,
  onSave,
  checkHarvest,
}: {
  open: boolean
  onClose: () => void
  initial?: HarvestLog
  onSave: (input: Omit<CreateHarvestLogInput, 'fieldId' | 'seasonYear'>) => Promise<unknown>
  checkHarvest: (harvestedOn: string) => Promise<CanadaGapBlockingApplication[]>
}) {
  const [harvestedOn, setHarvestedOn] = useState(initial?.harvestedOn ?? '')
  const [productVariety, setProductVariety] = useState(initial?.productVariety ?? '')
  const [phiVerified, setPhiVerified] = useState(initial?.phiVerified ?? false)
  const [siteAssessed, setSiteAssessed] = useState(initial?.siteAssessed ?? false)
  const [quantityHarvested, setQuantityHarvested] = useState(initial ? String(initial.quantityHarvested) : '')
  const [quantityUnit, setQuantityUnit] = useState(initial?.quantityUnit ?? 'kg')
  const [packagingMaterials, setPackagingMaterials] = useState(initial?.packagingMaterials ?? '')
  const [storedOn, setStoredOn] = useState(initial?.storedOn ?? '')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { org } = useAuth()
  const { farmId, fieldId } = useParams<{ farmId: string; fieldId: string }>()
  const check = useEntryCheck(org?.id, farmId, {
    form: 'p2',
    fieldId: fieldId ?? '',
    entry: {
      harvestedOn: harvestedOn || null,
      productVariety,
      phiVerified,
      siteAssessed,
      quantityHarvested: draftNumber(quantityHarvested),
      quantityUnit,
      packagingMaterials,
    },
  })

  // When a recorded H1/H2 EAHD blocks this harvest date, "PHI verified" can't be true: untick it.
  // The products and EAHDs themselves are listed by the CanadaGAP check panel.
  useEffect(() => {
    if (!harvestedOn) return
    let cancelled = false
    checkHarvest(harvestedOn)
      .then((found) => !cancelled && found.length > 0 && setPhiVerified(false))
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [harvestedOn, checkHarvest])

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!harvestedOn || !productVariety.trim() || !quantityHarvested) return
    setSubmitting(true)
    setError(null)
    try {
      await onSave({
        productVariety: productVariety.trim(),
        phiVerified,
        siteAssessed,
        harvestedOn,
        quantityHarvested: Number(quantityHarvested),
        quantityUnit,
        packagingMaterials: packagingMaterials.trim() || null,
        storedOn: storedOn || null,
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save harvest log.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{initial ? 'Edit harvest' : 'Log a harvest'}</DialogTitle>
      <div className="flex flex-wrap gap-1.5">
        <ComplianceBadge framework="CanadaGAP" formCode="P2" formName="Harvesting and Storing Product" {...checkBadgeProps(check)} />
      </div>
      <form onSubmit={handleSubmit}>
        <DialogBody>
          {error && <Text className="mb-4 text-red-600 dark:text-red-400">{error}</Text>}
          <FieldGroup>
            <FormField>
              <Label>Date harvested</Label>
              <Input type="date" value={harvestedOn} onChange={(e) => setHarvestedOn(e.target.value)} required autoFocus />
            </FormField>
            <FormField>
              <Label>Product and variety</Label>
              <Input
                value={productVariety}
                onChange={(e) => setProductVariety(e.target.value)}
                placeholder="e.g. Duke Blueberry"
                required
              />
              <InputWarning result={check} input="productVariety" />
            </FormField>
            <div className="flex gap-3">
              <FormField className="flex-1">
                <Label>Quantity harvested</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={quantityHarvested}
                  onChange={(e) => setQuantityHarvested(e.target.value)}
                  placeholder="0"
                  required
                />
                <InputWarning result={check} input="quantityHarvested" />
              </FormField>
              <FormField className="w-28">
                <Label>Unit</Label>
                <Select value={quantityUnit} onChange={(e) => setQuantityUnit(e.target.value)}>
                  <option value="kg">kg</option>
                  <option value="lb">lb</option>
                  <option value="flat">flat</option>
                  <option value="pint">pint</option>
                  <option value="clamshell">clamshell</option>
                </Select>
              </FormField>
            </div>
            <FormField>
              <Label>Packaging materials</Label>
              <Input
                value={packagingMaterials}
                onChange={(e) => setPackagingMaterials(e.target.value)}
                placeholder="e.g. 6L clamshells"
              />
              <InputWarning result={check} input="packagingMaterials" />
            </FormField>
            <FormField>
              <Label>Date put into storage</Label>
              <Input type="date" value={storedOn} onChange={(e) => setStoredOn(e.target.value)} />
            </FormField>
            <div className="space-y-3 pt-1">
              <label className="flex items-center gap-3">
                <Checkbox checked={phiVerified} onChange={setPhiVerified} />
                <span className="text-sm text-zinc-700 dark:text-zinc-300">PHI/EAHD/DAA met (H1/H2/H3 verified)</span>
              </label>
              <label className="flex items-center gap-3">
                <Checkbox checked={siteAssessed} onChange={setSiteAssessed} />
                <span className="text-sm text-zinc-700 dark:text-zinc-300">Production site assessed — no obvious contamination</span>
              </label>
            </div>
          </FieldGroup>
          <EntryCheckPanel result={check} formCode="P2" />
        </DialogBody>
        <DialogActions>
          <div className="sm:mr-auto">
            <ComplianceBadge framework="CanadaGAP" formCode="P2" formName="Harvesting and Storing Product" placement="above" {...checkBadgeProps(check)} />
          </div>
          <Button plain onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export function HarvestLogsPage() {
  const { farmId, fieldId } = useParams<{ farmId: string; fieldId: string }>()
  const { org } = useAuth()
  const { getFarm } = useFarms()
  const currentYear = new Date().getFullYear()
  const [seasonYear, setSeasonYear] = useState(currentYear)
  const [dialog, setDialog] = useState<{ log?: HarvestLog } | null>(null)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)

  const { field, loading: fieldLoading, error: fieldError } = useField(org?.id, farmId, fieldId)
  const { logs, loading: logsLoading, error: logsError, addLog, updateLog } = useHarvestLogs(org?.id, farmId, fieldId, seasonYear)
  const { status, reload: reloadStatus } = useCanadaGapStatus(org?.id, farmId, fieldId, seasonYear)
  const checkHarvestDate = useCallback(
    async (harvestedOn: string) =>
      org?.id && farmId && fieldId ? (await checkHarvest(org.id, farmId, fieldId, harvestedOn)).blocking : [],
    [org?.id, farmId, fieldId],
  )

  const farm = farmId ? getFarm(farmId) : undefined
  const backHref = `/farms/${farmId}/fields/${fieldId}`
  const loading = fieldLoading || logsLoading
  const error = fieldError || logsError

  const years = [currentYear - 2, currentYear - 1, currentYear]

  async function handleExport() {
    if (!org?.id || !farmId || !fieldId) return
    setExporting(true)
    setExportError(null)
    try {
      await exportP2(org.id, farmId, seasonYear, fieldId)
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed.')
    } finally {
      setExporting(false)
    }
  }

  async function saveLog(input: Omit<CreateHarvestLogInput, 'fieldId' | 'seasonYear'>) {
    if (!fieldId) return
    const log = dialog?.log ? await updateLog(dialog.log.id, input) : await addLog({ fieldId, seasonYear, ...input })
    await reloadStatus()
    if (log) setSaved(log.id)
  }

  // The after-save notice is about one entry: clear it when another dialog opens or the season changes.
  useEffect(() => {
    if (dialog) setSaved(null)
  }, [dialog])
  useEffect(() => setSaved(null), [seasonYear])

  return (
    <Shell>
      <Link href={backHref} className="inline-flex items-center gap-1 text-sm/6 font-medium text-zinc-500 dark:text-zinc-400">
        <ChevronLeftIcon className="size-4" />
        {field?.name ?? farm?.name ?? 'Field'}
      </Link>

      {error ? (
        <div className="mt-6 rounded-lg border border-dashed border-red-300 p-10 text-center dark:border-red-900">
          <Text>{error}</Text>
        </div>
      ) : loading ? (
        <div className="mt-6 rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
          <Text>Loading…</Text>
        </div>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
            <div>
              <Heading>{field?.name} — Harvest logs</Heading>
              <div className="flex flex-wrap gap-1.5">
                <ComplianceBadge framework="CanadaGAP" formCode="P2" formName="Harvesting and Storing Product" {...canadaGapBadgeProps(status, 'p2')} />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Select value={seasonYear} onChange={(e) => setSeasonYear(Number(e.target.value))} className="w-28">
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Select>
              <Button outline onClick={handleExport} disabled={exporting || logs.length === 0}>
                {exporting ? 'Exporting…' : 'Export P2'}
              </Button>
              <Button href={`/farms/${farmId}/log/harvest?field=${fieldId}`}>Log harvest</Button>
            </div>
          </div>

          {exportError && <Text className="mt-3 text-red-600 dark:text-red-400">{exportError}</Text>}
          {saved && status && (
            <SavedEntryNotice formCode="P2" issues={[issueFor(status.forms.p2, saved)].filter((i) => i !== undefined)} onDismiss={() => setSaved(null)} />
          )}

          {logs.length === 0 ? (
            <div className="mt-8 rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
              <Text>No harvest logs for {seasonYear}.</Text>
            </div>
          ) : (
            <ul className="mt-8 -mb-8">
              {logs.map((log, index) => (
                <li key={log.id}>
                  <div className="relative pb-8">
                    {index < logs.length - 1 && (
                      <span className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-zinc-200 dark:bg-zinc-700" aria-hidden="true" />
                    )}
                    <div className="relative flex gap-4">
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-zinc-100 ring-1 ring-zinc-950/10 dark:bg-zinc-800 dark:ring-white/10">
                        <span className="size-2 rounded-full bg-zinc-400 dark:bg-zinc-500" />
                      </div>
                      <button
                        type="button"
                        onClick={() => setDialog({ log })}
                        className="-m-2 min-w-0 flex-1 rounded-lg p-2 pt-2.5 text-left hover:bg-zinc-950/[2.5%] dark:hover:bg-white/[2.5%]"
                      >
                        <p className="text-sm font-semibold text-zinc-950 dark:text-white">{formatDate(log.harvestedOn)}</p>
                        <p className="mt-0.5 text-sm text-zinc-700 dark:text-zinc-300">
                          {log.productVariety} — {log.quantityHarvested} {log.quantityUnit}
                        </p>
                        <p className="mt-0.5 text-sm text-zinc-500 dark:text-zinc-400">
                          {log.phiVerified ? 'PHI verified' : <span className="text-amber-600 dark:text-amber-400">PHI not verified</span>}
                          {' · '}
                          {log.siteAssessed ? 'Site assessed' : <span className="text-amber-600 dark:text-amber-400">Site not assessed</span>}
                          {log.packagingMaterials && <> · {log.packagingMaterials}</>}
                          {log.storedOn && <> · Stored {formatDate(log.storedOn)}</>}
                        </p>
                        <EntryIssues issue={issueFor(status?.forms.p2, log.id)} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {dialog && (
            <HarvestLogDialog
              key={dialog.log?.id ?? 'new'}
              open
              onClose={() => setDialog(null)}
              initial={dialog.log}
              onSave={saveLog}
              checkHarvest={checkHarvestDate}
            />
          )}
        </>
      )}
    </Shell>
  )
}
